import { SIGNATURE_ATTR } from './mailTypes'

/**
 * Gmail imza görselleri (`ci*.googleusercontent.com/mail-sig`) bizim sitemizi
 * Referer olarak görünce 429 döner. İstek referanssız giderse görsel açılır.
 */
export function anonymizeImages(root: ParentNode) {
  for (const img of root.querySelectorAll('img')) {
    if (img.getAttribute('referrerpolicy') === 'no-referrer') continue
    const src = img.getAttribute('src')
    img.setAttribute('referrerpolicy', 'no-referrer')
    if (src && /^https?:/i.test(src)) {
      img.removeAttribute('src')
      img.setAttribute('src', src)
    }
  }
}

/**
 * Görüntüleme için gövde: görseller referanssız istenir; betikler ve olay öznitelikleri (onload…) atılır.
 * Sandbox zaten çalıştırmaz, ama atılmazsa tarayıcı her ileti için "Blocked script execution" uyarısı basar.
 */
export function withAnonymousImages(html: string): string {
  if (!/<img\b|<script\b|\son[a-z]+\s*=/i.test(html)) return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  for (const el of doc.querySelectorAll('script')) el.remove()
  for (const el of doc.body.querySelectorAll('*')) {
    for (const attr of [...el.attributes]) if (/^on/i.test(attr.name)) el.removeAttribute(attr.name)
  }
  anonymizeImages(doc)
  return doc.body.innerHTML
}

/** Baştan sona tek bir HTML parçası gibi görünen düz metin (ör. "<table …> … </table>"). */
export function looksLikeHtmlSource(text: string) {
  const s = text.trim()
  return /^<(?:!doctype|[a-z][\w-]*)[\s>/]/i.test(s) && /(?:<\/[a-z][\w-]*>|\/>)$/i.test(s)
}

/**
 * Editöre kod olarak yapıştırılmış imzayı (ör. VS Code'un renklendirdiği HTML) gerçek HTML'e çevirir;
 * görünen metni HTML kaynağı olan imza kaydı bu sayede önizlemede ve gönderimde düzgün çıkar.
 */
export function normalizeSignature(html: string): string {
  if (!html.includes('&lt;')) return html
  const text = new DOMParser().parseFromString(html, 'text/html').body.textContent ?? ''
  return looksLikeHtmlSource(text) ? text.trim() : html
}

const BLOCK_TAGS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'div', 'p', 'ul', 'ol', 'li', 'blockquote', 'section', 'center', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'])

const escText = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\u00a0/g, '&nbsp;')
const escAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;')

/**
 * İmza HTML'ini düzenlenebilir hale getirir: blok etiketler (tablo, satır, hücre, div…) ayrı satırda ve
 * girintili, yalnız satır içi içerik taşıyan bloklar tek satırda. Anlam değişmez; yalnız boşluklar eklenir.
 */
export function formatHtml(html: string): string {
  const src = html.trim()
  if (!src) return ''
  const body = new DOMParser().parseFromString(src, 'text/html').body
  const lines: string[] = []

  const inline = (n: Node): string => {
    if (n.nodeType === Node.TEXT_NODE) return escText(n.textContent ?? '')
    if (n.nodeType === Node.ELEMENT_NODE) return (n as Element).outerHTML
    return ''
  }
  const openTag = (el: Element) =>
    `<${el.tagName.toLowerCase()}${[...el.attributes].map((a) => ` ${a.name}="${escAttr(a.value)}"`).join('')}>`
  const hasBlockChild = (el: Element) => [...el.children].some((c) => BLOCK_TAGS.has(c.tagName.toLowerCase()))

  const walk = (nodes: NodeListOf<ChildNode>, depth: number) => {
    const pad = '  '.repeat(depth)
    let run = ''
    const flush = () => {
      if (run.trim()) lines.push(pad + run.trim())
      run = ''
    }
    for (const n of nodes) {
      if (n.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((n as Element).tagName.toLowerCase())) {
        flush()
        const el = n as Element
        const tag = el.tagName.toLowerCase()
        if (hasBlockChild(el)) {
          lines.push(pad + openTag(el))
          walk(el.childNodes, depth + 1)
          lines.push(`${pad}</${tag}>`)
        } else {
          lines.push(pad + openTag(el) + el.innerHTML.replace(/\s+/g, ' ').trim() + `</${tag}>`)
        }
      } else {
        run += inline(n).replace(/\s+/g, ' ')
      }
    }
    flush()
  }

  walk(body.childNodes, 0)
  return lines.join('\n')
}

/** Strip previously appended signature block so we never double-append. */
export function stripSignature(html: string): string {
  const re = new RegExp(`<div[^>]*${SIGNATURE_ATTR}[^>]*>[\\s\\S]*?<\\/div>\\s*$`, 'i')
  return html.replace(re, '').replace(/(?:<br\s*\/?>\s*)+$/i, '').trimEnd()
}

export function wrapSignature(signatureHtml: string): string {
  const inner = signatureHtml.trim()
  if (!inner) return ''
  return `<div ${SIGNATURE_ATTR}="1">${inner}</div>`
}

export function appendSignature(bodyHtml: string, signatureHtml: string): string {
  const base = stripSignature(bodyHtml)
  const sig = wrapSignature(signatureHtml)
  if (!sig) return base
  if (!base.trim()) return sig
  return `${base}<br/><br/>${sig}`
}

/** Yeni/yanıt gövdesi: imlecin imza bloğunun içine düşmemesi için başta boş bir paragraf bırakılır. */
export function buildComposeBody(signatureHtml: string, quoted?: string): string {
  const parts = ['<p><br/></p>']
  if (quoted?.trim()) parts.push(quoted.trim())
  return appendSignature(parts.join(''), signatureHtml)
}

export function htmlToSnippet(html: string, max = 120): string {
  const text = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
  if (text.length <= max) return text
  return `${text.slice(0, max - 1)}…`
}

export function quoteMessage(from: string, createdAt: string, bodyHtml: string): string {
  return `<br/><blockquote style="border-left:2px solid var(--border);padding-left:0.75rem;margin:0;color:var(--muted-foreground)">
<p><em>${from} — ${createdAt}</em></p>
${bodyHtml}
</blockquote>`
}
